import dayjs from 'dayjs';
import { notification } from 'antd';
import { MemberProfile } from '../model/members/MemberProfile';
import { MembershipCategory } from '../model/members/MembershipCategory';
import { PointOfSaleItem } from '../model/pointofsale/PointOfSaleItem';
import { PointOfSaleTransactionItem } from '../model/pointofsale/PointOfSaleTransactionItem';
import { CreatePointOfSaleTransactionRequest } from '../model/pointofsale/CreatePointOfSaleTransactionRequest';
import { ReceiptPrinterService } from '../services/printing/ReceiptPrinterService';
import { MemberPromotion } from '../model/pointofsale/MemberPromotion';

/**
 * Update member with new category and/or validTo date
 */
export const updateMemberDetails = async (
  member: MemberProfile,
  memberService: any,
  category: MembershipCategory | null,
  validTo: string | null,
  onMemberUpdated?: () => void
): Promise<boolean> => {
  if (!category) return false;

  const hasCategoryChange = category.name !== member.membershipCategory.name;
  const hasValidToChange = validTo && (
    !member.validTo || !dayjs(validTo).isSame(dayjs(member.validTo), 'day')
  );

  if (!hasCategoryChange && !hasValidToChange) {
    return false;
  }

  try {
    notification.info({
      message: 'Updating Member',
      description: 'Updating member details...'
    });

    const updateData: any = {
      ...member,
      membershipCategory: category
    };

    if (validTo) {
      updateData.validTo = validTo;
    }

    await memberService.update(updateData);

    notification.success({
      message: 'Member Updated',
      description: 'Member details updated successfully'
    });

    if (onMemberUpdated) {
      onMemberUpdated();
    }

    return true;
  } catch (error) {
    console.error('Error updating member:', error);
    notification.warning({
      message: 'Warning',
      description: 'Transaction processed but failed to update member details'
    });
    return false;
  }
};

/**
 * Create a transaction item from a POS item and amount
 */
export const createTransactionItem = (
  posItem: PointOfSaleItem,
  amount: number
): PointOfSaleTransactionItem => {
  const updatedItem = {
    ...posItem,
    price: amount
  };

  return {
    item: updatedItem,
    quantity: 1,
    overridePrice: amount
  };
};

/**
 * Create a transaction request
 */
export const createTransactionRequest = (
  membershipId: string,
  items: PointOfSaleTransactionItem[],
  amount: number,
  transactionStatus: string
): CreatePointOfSaleTransactionRequest => {
  return {
    membershipId,
    items,
    amount,
    bookingSlots: [],
    paymentTransactionStatus: transactionStatus
  };
};

/**
 * Process receipt printing for completed transactions
 */
export const processReceipt = async (
  transaction: any,
  receiptPrinterService: any,
  transactionStatus: string,
  memberName: string
): Promise<void> => {
  if (transactionStatus === 'Completed') {
    const receiptData = ReceiptPrinterService.createReceiptDataFromTransaction(transaction, memberName);
    await receiptPrinterService.printReceipt(receiptData, true);
  }
};

/**
 * Process receipt printing for an applied promotion (FORT-318).
 *
 * Same rule as processReceipt: only a paid transaction gets a receipt. An Unpaid promotion is a
 * recorded debt, not a completed sale, so there is nothing to hand the member.
 */
export const processPromotionReceipt = async (
  applied: MemberPromotion,
  posItemName: string,
  receiptPrinterService: any,
  transactionStatus: string,
  memberName: string,
  membershipId: string
): Promise<void> => {
  // `== null` not `!id` — a legitimate transaction id is never 0, but the explicit null check keeps
  // this honest if that ever changes.
  if (transactionStatus !== 'Completed' || applied.paymentTransactionId == null) return;

  const receiptData = ReceiptPrinterService.createReceiptDataFromPromotion(
    applied, posItemName, memberName, membershipId, transactionStatus);
  await receiptPrinterService.printReceipt(receiptData, true);
};

